import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { NuvoText } from '@nuvolo/nuux-core/components/NuvoText';
import { NuvoBasicDropDown } from '@nuvolo/nuux-core/components/NuvoDropDown';
import styled from 'styled-components';
import { color } from '@nuvolo/nuux-theme/utils';
import { hasKey } from '#/common/utils/typeGuards';
import { NuvoBox } from '@nuvolo/nuux-core/components/NuvoBox';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { NuvoTextField } from '@nuvolo/nuux-core/components/NuvoTextField';
import InfoMessageWithSeeMore from '../InfoMessageWithSeeMore';

const MappingRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 16px;
  align-items: center;
`;

const HeaderColumnBox = styled.div`
  margin-bottom: 12px;
`;

const ColumnBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  height: 7vh;
`;

const StyledArrowForwardIcon = styled(ArrowForwardIcon)`
  padding-bottom: 30px;
  height: 100%;
  font-size: 18px;
  color: ${color('primary', 'dark')};
`;

const ErrorText = styled(NuvoText)`
  color: ${color('error', 'middle')};
`;

const RequiredMessageWrapper = styled.div`
  margin-bottom: 40px;
  padding-bottom: 10px;
  border-bottom: 1px solid ${color('primary', 'light3')};
`;

export interface OptionInfo {
  key: string;
  label: string;
}
export interface TargetField extends OptionInfo {
  isDropdown?: boolean;
  values?: OptionInfo[]; // For dropdown fields, key and label are taken as default values
  required?: boolean;
  unique?: boolean;
}

export interface ColumnsMapProps {
  sourceOptions: OptionInfo[];
  targetFields: TargetField[];
  mapping: Record<string, string>;
  setMapping: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  targetDropdownSelections?: Record<string, string>;
  setTargetDropdownSelections?: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  setIsValid?: React.Dispatch<React.SetStateAction<boolean>>;
  uniqueFieldsMessage?: string;
  uniqueFieldsMessageSeeMore?: string;
  sourceLabel?: string;
  targetLabel?: string;
  showInvalidMessage?: boolean;
}

export const unknownMappingValue = { key: 'unknown', label: 'Unknown' } as OptionInfo;

const ColumnMapper = ({
  sourceOptions,
  targetFields,
  mapping,
  setMapping,
  targetDropdownSelections,
  setTargetDropdownSelections,
  setIsValid,
  uniqueFieldsMessage,
  uniqueFieldsMessageSeeMore,
  sourceLabel,
  targetLabel,
  showInvalidMessage,
}: ColumnsMapProps) => {
  const [allFieldsValid, setAllFieldsValid] = useState<boolean>(false);

  const getTargetFieldKey = useCallback(
    (field: TargetField) => {
      return field.isDropdown === true && targetDropdownSelections
        ? targetDropdownSelections[field.key]
        : field.key;
    },
    [targetDropdownSelections],
  );

  const getFieldValidation = useCallback(
    (fieldRequired: boolean, fieldUnique: boolean, currentMapping: string) => {
      // Value Unknown is acceptable for required fields but not for unique fields
      return (
        !fieldRequired ||
        (!!currentMapping && (!fieldUnique || currentMapping !== unknownMappingValue.key))
      );
    },
    [],
  );

  const handleMappingChange = useCallback((fieldKey: string, selectedItem: unknown) => {
    if (hasKey(selectedItem, 'key')) {
      const selectedKey = (selectedItem as OptionInfo).key;
      setMapping((prev) => ({
        ...prev,
        [fieldKey]: selectedKey,
      }));
    }
  }, []);

  const handleTargetSelectionChanged = useCallback((fieldKey: string, selectedItem: unknown) => {
    if (setTargetDropdownSelections && hasKey(selectedItem, 'key')) {
      const selectedKey = (selectedItem as OptionInfo).key;
      setTargetDropdownSelections((prev) => ({
        ...prev,
        [fieldKey]: selectedKey,
      }));
    }
  }, []);

  const columnDataSource = useCallback(
    (targetFieldKey: string) => {
      const mappingValues = Object.values(mapping);

      // Filter out already mapped columns except for the current mapping
      return [
        { ...unknownMappingValue },
        ...sourceOptions.filter(
          (col) => !mappingValues.includes(col.key) || col.key === mapping[targetFieldKey],
        ),
      ];
    },
    [sourceOptions, mapping],
  );

  useEffect(() => {
    const allRequiredFieldsMapped = targetFields.every((field) => {
      return getFieldValidation(
        field.required === true,
        field.unique === true,
        mapping[getTargetFieldKey(field)],
      );
    });

    if (allRequiredFieldsMapped !== allFieldsValid) {
      setAllFieldsValid(allRequiredFieldsMapped);
    }
  }, [mapping, targetDropdownSelections]);

  // Notify parent component about overall validity
  useEffect(() => {
    if (setIsValid) {
      setIsValid(allFieldsValid);
    }
  }, [allFieldsValid, setIsValid]);

  return (
    <NuvoBox display="flex" flexDirection="column" id="tmm-section">
      {(sourceLabel || targetLabel) && (
        <MappingRow>
          {/* Left side - Label */}
          <HeaderColumnBox>
            <NuvoText type="header3">{sourceLabel || ''}</NuvoText>
          </HeaderColumnBox>

          {/* Middle - Empty, make it the same width as the Arrow below */}
          <div style={{ width: '18px' }} />

          {/* Right side - Label */}
          <HeaderColumnBox>
            <NuvoText type="header3">{targetLabel || ''}</NuvoText>
          </HeaderColumnBox>
        </MappingRow>
      )}
      {targetFields.map((field) => {
        // Hide target fields that are already selected in other dropdowns
        if (
          !field.isDropdown &&
          Object.values(targetDropdownSelections ?? {}).includes(field.key)
        ) {
          return null;
        }

        const targetFieldKey = getTargetFieldKey(field);
        const currentMapping = mapping[targetFieldKey];
        const isFieldValid = getFieldValidation(
          field.required === true,
          field.unique === true,
          currentMapping,
        );

        return (
          <>
            <MappingRow>
              {/* Left side - Options Selector */}
              <ColumnBox>
                <NuvoBasicDropDown
                  className="dropdown-field"
                  value={currentMapping}
                  dataSource={columnDataSource(targetFieldKey)}
                  displayExpr="label"
                  valueExpr="key"
                  showClearButton={false}
                  placeholder="Select a Column"
                  onSelectionChanged={(e) =>
                    handleMappingChange(
                      targetFieldKey,
                      hasKey(e, 'selectedItem') ? e.selectedItem : null,
                    )
                  }
                />
                {showInvalidMessage && !isFieldValid && (
                  <ErrorText type="small">This field is required</ErrorText>
                )}
              </ColumnBox>

              {/* Middle - Arrow */}
              <StyledArrowForwardIcon />

              {/* Right side - Target Fields */}
              <ColumnBox>
                {field.isDropdown === true ? (
                  <NuvoBasicDropDown
                    className="dropdown-field"
                    value={
                      targetDropdownSelections ? targetDropdownSelections[field.key] : undefined
                    }
                    dataSource={field.values || [{ key: field.key, label: field.label }]}
                    displayExpr="label"
                    valueExpr="key"
                    showClearButton={false}
                    onSelectionChanged={(e) =>
                      handleTargetSelectionChanged(
                        field.key,
                        hasKey(e, 'selectedItem') ? e.selectedItem : null,
                      )
                    }
                  />
                ) : (
                  <div className="organization-wrapper">
                    <NuvoTextField value={field.label} readOnly />
                  </div>
                )}
              </ColumnBox>
            </MappingRow>
            {uniqueFieldsMessage && field.unique && (
              <RequiredMessageWrapper>
                <InfoMessageWithSeeMore
                  mainMessage={uniqueFieldsMessage}
                  seeMoreMessage={uniqueFieldsMessageSeeMore}
                />
              </RequiredMessageWrapper>
            )}
          </>
        );
      })}
    </NuvoBox>
  );
};

export default React.memo(ColumnMapper);
